/**
 * Twilio Voice SDK `Device` singleton. Plain module-scope state (an ES
 * module already IS a singleton) rather than a class — matches the
 * `api/token.ts` convention for the one other "global mutable resource
 * outside React" this app has.
 *
 * Scope is intentionally narrow: register/connect/accept/reject/mute/DTMF
 * and token refresh. Everything that changes call *state* (hold/resume/
 * transfer/end/disposition) is a REST command against the backend's
 * conference-per-call orchestration — the SDK Call object here is never
 * asked to do more than control the browser's own WebRTC leg.
 *
 * Multi-call model (finish-first): exactly one ACTIVE call (`activeCall`);
 * every other inbound leg waits in `pendingIncoming` keyed by callId until
 * it is accepted (promoted to active), rejected, canceled by the backend
 * (answered elsewhere / ring timeout), or the device is destroyed. The SDK
 * single-call ringtone is disabled — `use-ringtone.ts` rings for the queue.
 *
 * Failures self-heal: a failed init, a failed token refresh, or an
 * unexpected unregister schedules a capped-exponential-backoff recovery
 * that re-mints the token and re-registers. Without this, a backend
 * restart while a tab is open leaves the softphone silently unreachable
 * until a manual page refresh.
 */
import { Device, type Call } from '@twilio/voice-sdk';
import { voiceApi } from '@/api/services/voice.api';
import type { IMintedVoiceToken } from '../types';

export interface VoiceDeviceHandlers {
    readonly onIncoming: (call: Call) => void;
    readonly onRegistered?: () => void;
    readonly onUnregistered?: () => void;
    readonly onError?: (error: Error) => void;
}

const RECOVERY_BASE_DELAY_MS = 2_000;
const RECOVERY_MAX_DELAY_MS = 30_000;

/** Capped exponential backoff: 2s, 4s, 8s, 16s, 30s, 30s, … */
export function recoveryDelayMs(attempt: number): number {
    return Math.min(RECOVERY_BASE_DELAY_MS * 2 ** attempt, RECOVERY_MAX_DELAY_MS);
}

let device: Device | null = null;
let activeCall: Call | null = null;
const pendingIncoming = new Map<string, Call>();
let lastHandlers: VoiceDeviceHandlers | null = null;
let recoveryTimer: ReturnType<typeof setTimeout> | null = null;
let recoveryAttempt = 0;

export function getVoiceDevice(): Device | null {
    return device;
}

/** The callId a Call is keyed by — the backend's custom param, falling back
 *  to the Twilio CallSid (always present on a real incoming leg). */
export function resolveCallId(call: Call): string | null {
    return call.customParameters.get('callId') ?? call.parameters.CallSid ?? null;
}

export function getActiveCall(): Call | null {
    return activeCall;
}

/** Ringing (not yet answered) inbound legs, in arrival order. */
export function listPendingCallIds(): string[] {
    return [...pendingIncoming.keys()];
}

/**
 * Answers a queued inbound leg and promotes it to the one active call.
 * Returns `true` when the accept was honored, `false` when it was refused
 * — an unknown callId, or a call already active. The active-call guard
 * enforces the finish-first single-active invariant on this SYNCHRONOUS
 * source of truth (the store's `activeCall` lags — it is written only in
 * the async SDK 'accept' handler), so two answers fired inside the
 * media-setup window can't both promote.
 */
export function acceptPending(callId: string): boolean {
    if (activeCall) return false;
    const call = pendingIncoming.get(callId);
    if (!call) return false;
    pendingIncoming.delete(callId);
    activeCall = call;
    call.accept();
    return true;
}

export function rejectPending(callId: string): void {
    const call = pendingIncoming.get(callId);
    if (!call) return;
    pendingIncoming.delete(callId);
    call.reject();
}

export function hangupActive(): void {
    activeCall?.disconnect();
}

/** True when a live connection was muted; false = nothing to act on. */
export function muteActive(muted: boolean): boolean {
    if (!activeCall) return false;
    activeCall.mute(muted);
    return true;
}

export function sendDigits(digits: string): void {
    activeCall?.sendDigits(digits);
}

function cancelRecovery(): void {
    if (recoveryTimer) {
        clearTimeout(recoveryTimer);
        recoveryTimer = null;
    }
    recoveryAttempt = 0;
}

/** One pending attempt at a time; each failure schedules the next. */
function scheduleRecovery(): void {
    if (recoveryTimer || !lastHandlers) return;
    const delay = recoveryDelayMs(recoveryAttempt);
    recoveryAttempt += 1;
    recoveryTimer = setTimeout(() => {
        recoveryTimer = null;
        void attemptRecovery();
    }, delay);
}

async function attemptRecovery(): Promise<void> {
    const handlers = lastHandlers;
    if (!handlers) return; // destroyed while waiting

    try {
        const minted = await voiceApi.mintToken();
        if (!device) {
            // Mount-time init failed (e.g. backend was down) — retry from
            // scratch, unless a destroy/re-init superseded this attempt.
            if (lastHandlers !== handlers) return;
            await initVoiceDevice(minted, handlers);
            return;
        }
        device.updateToken(minted.token);
        if (minted.canReceive && device.state !== Device.State.Registered) {
            await device.register();
        }
        recoveryAttempt = 0;
    } catch (error: unknown) {
        console.warn('[voice] device recovery attempt failed', error);
        scheduleRecovery();
    }
}

/**
 * (Re)creates the Device from an already-minted token. Idempotent — safe to
 * call once per mount. The caller mints (the facade needs the `provider`
 * discriminator off the same response to pick this module at all), so minting
 * again here would double every init's token requests.
 */
export async function initVoiceDevice(
    minted: IMintedVoiceToken,
    handlers: VoiceDeviceHandlers,
): Promise<Device> {
    lastHandlers = handlers;

    const next = new Device(minted.token, { logLevel: 'error', allowIncomingWhileBusy: true });
    // The SDK ringtone only understands a single incoming call (and stays
    // silent while busy anyway) — `use-ringtone.ts` rings for the queue.
    next.audio?.incoming(false);

    next.on('registered', () => {
        cancelRecovery();
        handlers.onRegistered?.();
    });
    next.on('unregistered', () => {
        handlers.onUnregistered?.();
        // Fires on token expiry / transport loss too — win the registration
        // back instead of staying silently unreachable. destroyVoiceDevice
        // clears `lastHandlers` first, so teardown never schedules this.
        if (device === next) scheduleRecovery();
    });
    next.on('error', (error: Error) => handlers.onError?.(error));
    next.on('incoming', (call: Call) => {
        const callId = resolveCallId(call);
        if (!callId) {
            // Unidentifiable leg — nothing to key it by; refuse rather than
            // let a phantom entry get stuck in the map forever.
            call.reject();
            return;
        }
        pendingIncoming.set(callId, call);
        // Same-call guard: a later leg reusing the id must not be evicted
        // by a stale event from this one.
        const removeFromPending = (): void => {
            if (pendingIncoming.get(callId) === call) pendingIncoming.delete(callId);
        };
        call.on('cancel', removeFromPending);
        call.on('reject', removeFromPending);
        call.on('accept', () => {
            // Backstop for accepts not routed through acceptPending().
            removeFromPending();
            activeCall = call;
        });
        call.on('disconnect', () => {
            removeFromPending();
            if (activeCall === call) activeCall = null;
        });
        handlers.onIncoming(call);
    });
    next.on('tokenWillExpire', () => {
        voiceApi
            .mintToken()
            .then((refreshed) => next.updateToken(refreshed.token))
            .catch((error: unknown) => {
                handlers.onError?.(error as Error);
                scheduleRecovery();
            });
    });

    device = next;
    if (minted.canReceive) {
        try {
            await next.register();
        } catch (error: unknown) {
            scheduleRecovery();
            throw error;
        }
    }
    return next;
}

/** Dials the org's TwiML App for an already-created outbound Call row.
 *  The outbound leg becomes the one active call. */
export async function connectOutboundLeg(callId: string): Promise<Call> {
    if (!device) throw new Error('Voice device is not initialized');
    const call = await device.connect({ params: { callId } });
    activeCall = call;
    call.on('disconnect', () => {
        if (activeCall === call) activeCall = null;
    });
    return call;
}

export function destroyVoiceDevice(): void {
    activeCall = null;
    pendingIncoming.clear();
    lastHandlers = null;
    cancelRecovery();
    if (!device) return;
    // Cleared before destroy() so its own 'unregistered' event can't
    // schedule a recovery for a device we are tearing down.
    const dying = device;
    device = null;
    dying.destroy();
}
